import moment from 'moment';

// Ticket documents for the Time Card Approval tab.
//
// A field ticket the client signs does not map one-to-one onto a work day and
// shift: the first days of a job run a day crew only and get signed as one
// document, a weekend may come back as a single packet for both crews, and
// the paper backup (sign-in scans in SharePoint 02.06) is filed however the
// field filed it. A ticket DOCUMENT is therefore its own thing: the set of
// (work date, shift) cells it covers, the SharePoint backup files that
// support it, a Claude review of every covered cell against that backup, and
// the one DocuSign envelope that goes out for all of it. When that envelope is
// signed, every cell it covers reads Signed on the tracker.
//
// The day list never shows documents — it keeps its per-shift chips, and a
// chip covered by a document takes its status from the document. The
// cell -> document mapping lives in the Ticket Map modal.
//
// The document rides the Billing tab's Forecast_User_Data rows, chunked like
// the Custom Ticket drafts (billingManualTicket.js) because it outgrows the
// 1024-char Notes column:
//   fieldName 'tcdoc'  rowId 'tcdoc_<id>#<i>'
//     { id, cells: ['YYYY-MM-DD|Day', …], files: [{ id, driveId, name, path, auto? }],
//       af: [cells auto-attach already filled], skip: [file ids taken off],
//       st, h: [stamps], ai: { hash, at, by, runs: { '<cell>': reviewId } }?,
//       ack: { '<reviewId>#<findingIndex>': { u, t, n } },
//       ap: { u, e, t, hash }?   — the Strike-side approval to send, pinned
//                                  to the data it was given on
//       signed: { name, path, url, t, u }? — a signed copy uploaded by hand
//                                  (paper signature) instead of DocuSign
//       kind?: 'summary'           — a week summary only (isSummaryDoc)
//       del? }
// Chunk 0's Value is '1/<count>'; stale higher chunks from an older, longer
// save are ignored. A removed document is chunk 0 rewritten as { id, del: 1 }.
//
// The Claude reviews themselves live in PC.Field_Ticket_Review (one row per
// cell per run — readFieldTicketReviews): a review takes minutes and its
// result is tens of KB. Acknowledgments key on the review row, so re-running
// the check starts every cell's acknowledgments over.

export const DOC_SHIFTS = ['Day', 'Night'];
// Which tickets a signer signs: their shift's, or everything.
export const SIGNER_ROLES = ['Day', 'Night', 'All'];
// And WHEN they sign — orthogonal to the role, because the two questions are
// independent: the client's weekday day-shift rep is off at the weekend while
// the people who sign everything still sign everything. Stored as a short key
// on the signer row and omitted entirely at the default, because the job's
// whole settings object is one 1024-character Notes cell and four signers
// already put it near 800.
export const SIGNER_DAYS = [
    { id: '', label: 'All days' },
    { id: 'W', label: 'Weekdays' },
    { id: 'E', label: 'Weekends' },
];
export const signerDays = (d) => (d === 'W' || d === 'E' ? d : '');
/** Saturday or Sunday, off a 'YYYY-MM-DD' work date. Noon local so no time
 *  zone can push the date onto the day either side of it. */
export const isWeekend = (d) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(d || ''))) return false;
    const day = new Date(`${d}T12:00:00`).getDay();
    return day === 0 || day === 6;
};
/** Does a signer's day scope cover any of these work dates? No dates asked
 *  about (a cover page that isn't about one day) means yes. */
export const coversDates = (days, dates) => {
    const scope = signerDays(days);
    if (!scope) return true;
    const list = (Array.isArray(dates) ? dates : [dates]).filter(Boolean);
    if (!list.length) return true;
    return list.some((d) => (scope === 'E' ? isWeekend(d) : !isWeekend(d)));
};
// What the upload pickers accept — mirrors EXT_MIME in writeJobTicketFile.js;
// keep the two lists in step.
export const DOC_ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.heic,.xlsx,.xlsm,.xls,.csv,.doc,.docx,.msg,.eml,.txt';
export const MAX_DOC_BYTES = 15 * 1024 * 1024;
export const DOC_STAGES = ['Draft', 'Validated', 'Sent for Signature', 'Signed', 'Declined'];

const CHUNK = 900;

export const cellKey = (d, sh) => `${d}|${sh}`;
export const splitCell = (k) => {
    const [d, sh] = String(k).split('|');
    return { d, sh };
};
// Date order, Day before Night — the order tickets print in the packet.
export const sortCells = (cells) => [...new Set(cells || [])].sort((a, b) => {
    const x = splitCell(a); const y = splitCell(b);
    return x.d.localeCompare(y.d) || DOC_SHIFTS.indexOf(x.sh) - DOC_SHIFTS.indexOf(y.sh);
});

export const newDocId = () => (
    Date.now().toString(36).slice(-5) + Math.random().toString(36).slice(2, 5)
);

// ── Chunked storage ──

export const chunkRows = (fieldName, key, obj) => {
    const json = JSON.stringify(obj);
    const n = Math.max(1, Math.ceil(json.length / CHUNK));
    return Array.from({ length: n }, (_, i) => ({
        fieldName,
        rowId: `${key}#${i}`,
        value: `${i + 1}/${n}`,
        notes: json.slice(i * CHUNK, (i + 1) * CHUNK),
    }));
};

const parseChunked = (userRows, fieldName) => {
    const groups = {};
    (userRows || []).forEach((x) => {
        if (x?.Forecast_Field_Name !== fieldName) return;
        const m = /^(.+)#(\d+)$/.exec(String(x.Forecast_Row_ID || ''));
        if (!m) return;
        if (!groups[m[1]]) groups[m[1]] = {};
        groups[m[1]][Number(m[2])] = x;
    });
    const out = [];
    Object.values(groups).forEach((chunks) => {
        const first = chunks[0];
        if (!first) return;
        const n = Number(String(first.Forecast_Value || '').split('/')[1]) || 1;
        let json = '';
        for (let i = 0; i < n; i += 1) {
            if (!chunks[i]) return; // a missing chunk makes the blob unreadable
            json += chunks[i].Notes || '';
        }
        try {
            const v = JSON.parse(json);
            if (v && v.id) out.push(v);
        } catch (e) { /* malformed blob — ignore */ }
    });
    return out;
};

export const docRows = (doc) => chunkRows('tcdoc', `tcdoc_${doc.id}`, doc);
export const removedDocRows = (doc) => [{
    fieldName: 'tcdoc', rowId: `tcdoc_${doc.id}#0`, value: '1/1', notes: JSON.stringify({ id: doc.id, del: 1 }),
}];

/** Live documents, oldest first (the first to claim a cell keeps it). */
export const parseDocs = (userRows) => parseChunked(userRows, 'tcdoc')
    .filter((d) => !d.del && Array.isArray(d.cells))
    .map((d) => ({
        ...d,
        cells: sortCells(d.cells.filter((k) => /^\d{4}-\d{2}-\d{2}\|(Day|Night)$/.test(k))),
        files: Array.isArray(d.files) ? d.files : [],
        h: Array.isArray(d.h) ? d.h : [],
        ack: d.ack && typeof d.ack === 'object' ? d.ack : {},
        st: DOC_STAGES.includes(d.st) ? d.st : 'Draft',
    }))
    .sort((a, b) => String(a.h[0]?.t || '').localeCompare(String(b.h[0]?.t || '')) || a.id.localeCompare(b.id));

/** The history stamps a document keeps are capped like sheetNotes'. */
export const capHistory = (h, max = 30) => (h.length > max ? h.slice(h.length - max) : h);

// ── Naming ──

const md = (d) => moment(d, 'YYYY-MM-DD').format('M/D');
const runs = (dates) => {
    const out = [];
    dates.forEach((d) => {
        const last = out[out.length - 1];
        if (last && moment(d, 'YYYY-MM-DD').diff(moment(last[1], 'YYYY-MM-DD'), 'days') === 1) last[1] = d;
        else out.push([d, d]);
    });
    return out.map(([a, b]) => (a === b ? md(a) : `${md(a)}–${md(b)}`)).join(', ');
};

/** "8/27–8/30 Day", "8/31 Day+Night", "9/1–9/3 Day, 9/2 Night". */
export const docName = (cells) => {
    const byShift = { Day: [], Night: [] };
    sortCells(cells).forEach((k) => { const { d, sh } = splitCell(k); byShift[sh].push(d); });
    const day = byShift.Day; const night = byShift.Night;
    if (!day.length && !night.length) return 'Empty ticket';
    if (day.join() === night.join()) return `${runs(day)} Day+Night`;
    return [day.length ? `${runs(day)} Day` : '', night.length ? `${runs(night)} Night` : ''].filter(Boolean).join(', ');
};

// ── Coverage and status ──

/**
 * cell -> the live document covering it. A cell claimed twice (two people
 * building documents at once) stays with the older document; the newer one
 * is reported in conflicts so the map can say so.
 *
 * Unless only the newer one has gone to the client: a sent or signed ticket
 * takes the cell from an older one that never went out, and the draft is the
 * conflict. A draft started on 9/28 for 54026071's 9/23–9/27 and never sent
 * held those days over the three signed tickets sent for them, and the day
 * list read them as missing.
 */
const OUT_STAGES = ['Sent for Signature', 'Signed', 'Declined'];
export const coverageMap = (docs) => {
    const map = {};
    const conflicts = [];
    // A supplement rides beside the day's ticket rather than owning the day
    // (isSupplementDoc); `extras` lists them per cell for the map's badges.
    const extras = {};
    const out = (doc) => OUT_STAGES.includes(doc.st);
    (docs || []).filter((doc) => !isSummaryDoc(doc) && !isSupplementDoc(doc)).forEach((doc) => doc.cells.forEach((k) => {
        const owner = map[k];
        if (!owner || owner.id === doc.id) map[k] = doc;
        else if (out(doc) && !out(owner)) {
            map[k] = doc;
            conflicts.push({ cell: k, doc: owner, owner: doc });
        } else conflicts.push({ cell: k, doc, owner });
    }));
    (docs || []).filter(isSupplementDoc).forEach((doc) => doc.cells.forEach((k) => { (extras[k] = extras[k] || []).push(doc); }));
    return { map, conflicts, extras };
};

// A crew's week can also go out from outside the Ticket Map, keyed 'Week-Day'
// / 'Week-Night' on the week-ending Sunday with no Doc_ID — the send endpoint's
// own kind for a combined weekly packet. It is the same send as a document's
// day or night part (that crew's tickets, Monday to Sunday), so it is indexed
// beside the documents, under a key no Doc_ID can spell ([a-z0-9] only), and
// partEnvelopes stands it on that part.
const WEEK_SHIFT = /^Week-(Day|Night)$/;
const weekKey = (sunday, sh) => `week:${sunday}:${sh}`;
/** The 'Week-Day' / 'Week-Night' envelope on one week ending, else null. */
export const weekCrewEnvelope = (sigByDoc, sunday, sh) => sigByDoc?.[weekKey(sunday, sh)] || null;
/** The Sunday closing out a work date's Monday–Sunday week. */
export const weekEnding = (d) => {
    const m = moment(String(d || '').slice(0, 10), 'YYYY-MM-DD');
    return m.isValid() ? m.add((7 - m.day()) % 7, 'days').format('YYYY-MM-DD') : '';
};

/**
 * Newest envelope per document id, plus the newest week envelope per
 * (week ending, crew) — the read path is newest-first.
 */
export const envelopesByDoc = (envelopes) => {
    const m = {};
    (envelopes || []).forEach((e) => {
        const wk = WEEK_SHIFT.exec(String(e.Shift || ''));
        const k = e.Shift === 'Doc' ? e.Doc_ID : wk && weekEnding(e.Work_Date) ? weekKey(weekEnding(e.Work_Date), wk[1]) : null;
        if (k && !m[k]) m[k] = e;
    });
    return m;
};

/**
 * The stage an envelope puts its document in. A voided, expired or failed
 * envelope hands the document back to where it was before it went out, so
 * it can be corrected and sent again.
 */
export const stageFromEnvelope = (doc, env) => {
    const s = String(env?.Status || '').toLowerCase();
    if (s === 'completed') return 'Signed';
    if (s === 'declined') return 'Declined';
    if (s === 'sent' || s === 'delivered') return 'Sent for Signature';
    if (['voided', 'expired', 'failed'].includes(s)) return doc.ai ? 'Validated' : 'Draft';
    return doc.st;
};

/**
 * What a day chip shows for one cell when a document covers it, else null.
 *
 * A cell rides at most two sends and is tracked against both: its own crew's
 * — the day rep signing their week moves the day chips whatever the night
 * send is doing — and the week summary the same day is printed on, which the
 * sign-everything reps sign separately. Both went to the client, so the cell
 * is not Signed until both come back.
 *
 * `envs` is [{ part, env }] for the sends that have actually gone out, and
 * `env` the one the status line speaks for (leadEnvelope).
 */
export const cellDocStatus = (d, sh, coverage, sigByDoc) => {
    const doc = coverage?.map?.[cellKey(d, sh)];
    if (!doc) return null;
    const all = partEnvelopes(doc, sigByDoc);
    const parts = cellParts(doc, sh);
    const envs = parts.map((part) => ({ part, env: all[part] || null })).filter((x) => x.env);
    return {
        doc,
        parts,
        envs,
        env: leadEnvelope(envs),
        stage: stageAcross(doc, envs.map((x) => x.env), parts.length) || doc.st,
    };
};

// A document can only be edited (cells, files) before it goes out — after
// that the envelope carries a fixed PDF, and changing what the document
// claims would make the map disagree with what the client signed. A signed
// copy uploaded by hand freezes it the same way.
export const docLocked = (doc, env) => {
    const s = String(env?.Status || '').toLowerCase();
    if (['sent', 'delivered', 'completed', 'created'].includes(s)) return true;
    return Boolean(doc?.st === 'Signed' && doc?.signed);
};

// ── Signers ──
//
// The client's people do not all sign the same thing: one rep signs the day
// report, another the night report, and a couple of people sign everything.
// The job's billing settings hold that map ({ signers: [{ name, email,
// role }] }), and from it a ticket page gets one signature line per person
// who signs that shift, and an envelope gets every person whose role covers
// a shift on the document.

const emailOk = (x) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(x || '').trim());

/**
 * The job's signer map — [{ name, email, role }]. A job that only ever set
 * the single "client signer" reads as one person who signs everything, so
 * nothing changes for it.
 */
export const jobSigners = (settings) => {
    const list = Array.isArray(settings?.signers) ? settings.signers : [];
    const out = list
        .map((x) => ({
            name: String(x?.name || '').trim(),
            email: String(x?.email || '').trim(),
            role: SIGNER_ROLES.includes(x?.role) ? x.role : 'All',
            d: signerDays(x?.d),
        }))
        .filter((x) => emailOk(x.email));
    if (out.length) return out;
    const email = String(settings?.signerEmail || '').trim();
    return emailOk(email) ? [{ name: String(settings?.signerName || '').trim(), email, role: 'All', d: '' }] : [];
};

/**
 * The signature lines a ticket page prints for one shift, in order: the
 * shift's own rep first, then everyone who signs all. Each line's label is
 * what the DocuSign tab anchors on ("<label> - Signature"), so labels must be
 * distinct and no label may be a substring of another's. With nobody mapped
 * the page keeps the single line every ticket used to carry.
 *
 * `dates` is what the page is about — one work date for a day's ticket, the
 * whole list for a cover, null for a page that isn't about a date at all.
 * Signers whose day scope misses every one of them are left off: a Saturday
 * ticket carries only the people who sign weekends.
 */
export const signerSlots = (signers, shift, mode = 'full', dates = null) => {
    const list = signers || [];
    const slots = [];
    // 'All' is a whole-document summary, not a crew shift: it carries the
    // sign-everything people only, never an "All Shift Representative".
    if (shift !== 'All') {
        list.filter((x) => x.role === shift).forEach((x, i) => slots.push({
            label: `${shift} Shift Representative${i ? ` ${i + 1}` : ''}`, name: x.name, email: x.email, role: x.role, d: x.d,
        }));
    }
    // 'shift' mode is for the pages under a summary that the
    // sign-everything people already sign — they don't sign each page too.
    // The sign-everything people sign in series, in the map's order: the
    // first "Customer Representative" signs, then the second is notified
    // (Ryan Bakke, 10/13/26: the week summary goes to Brad Hewitt, then to
    // him — not both at once). `order` is the DocuSign routing order
    // (docusignShared.createEnvelope); a shift rep carries none and signs at
    // once, as before.
    if (mode !== 'shift') {
        list.filter((x) => x.role === 'All').forEach((x, i) => slots.push({
            label: `Customer Representative${i ? ` ${i + 1}` : ''}`, name: x.name, email: x.email, role: x.role, d: x.d, order: i + 1,
        }));
    }
    // The numbering above runs over the WHOLE role list, before the day scope
    // is applied, so a person's label is the same string on every page they
    // sign. Numbering the filtered list instead would make "Customer
    // Representative" mean Greg on a Tuesday page and Brad on a Saturday one
    // — and the DocuSign tab anchors on exactly that string, so Brad would
    // end up signing Greg's line.
    const onDates = dates ? slots.filter((x) => coversDates(x.d, dates)) : slots;
    if (onDates.length) return onDates;
    // A map that exists but has nobody for THESE dates prints no line at all.
    // Falling back to the generic line here would put an unattributed
    // signature block on a page nobody mapped is meant to sign.
    if (slots.length) return [];
    // Nobody mapped for these pages: the single line every ticket used to
    // carry, except in 'shift' mode, where an unsigned page is the point.
    return mode === 'shift' ? [] : [{ label: 'Customer Representative', name: '', email: '', role: 'All', d: '', order: 1 }];
};

/**
 * Who an envelope for these cells goes to: every signer whose role covers a
 * shift on the document, with the labels of the lines they sign — a
 * sign-everything person on a Day+Night packet has one label that appears
 * on both shifts' pages. Order of first appearance: day rep, everyone,
 * night rep. A sign-everything person carries their routing `order`
 * (signerSlots), so the week summary routes Brad then Ryan; the send
 * endpoint keeps it (billingSignature.js) and the envelope's second signer
 * waits for the first.
 */
export const docSigners = (settings, cells) => {
    // Walked cell by cell rather than shift by shift: a signer's day scope
    // turns on the work date, so a weekend-only document must not collect the
    // weekday rep just because it has a Day shift on it.
    const list = sortCells(cells).map((k) => splitCell(k));
    const signers = jobSigners(settings);
    const byEmail = {};
    const order = [];
    list.forEach(({ d, sh }) => signerSlots(signers, sh, 'full', d).forEach((slot) => {
        if (!slot.email) return;
        const k = slot.email.toLowerCase();
        if (!byEmail[k]) { byEmail[k] = { name: slot.name, email: slot.email, role: slot.role, anchors: [], ...(slot.order ? { order: slot.order } : {}) }; order.push(k); }
        if (!byEmail[k].anchors.includes(slot.label)) byEmail[k].anchors.push(slot.label);
    }));
    return order.map((k) => byEmail[k]);
};

/**
 * The signers an envelope can actually carry: every anchor a signer holds
 * that the built packet really printed. A ticket document can cover a shift
 * that produced no pages (a night with no hours), and DocuSign rejects the
 * whole envelope when an anchor string is missing — so the packet's own
 * labels, not the document's cells, decide who is on it.
 */
export const signersForLabels = (signers, labels) => {
    if (!Array.isArray(labels)) return signers || [];
    const have = new Set(labels);
    return (signers || [])
        .map((s) => ({ ...s, anchors: (s.anchors || []).filter((a) => have.has(a)) }))
        .filter((s) => s.anchors.length);
};

// ── Send parts ──
//
// A ticket does not go to the client as one document: the day crew's tickets
// go as one, the night crew's as another, and the week summary that covers
// both as a third. Each is its own DocuSign envelope, so the day rep can
// sign their week without waiting on the night rep, and each rides a Doc_ID
// of its own — the document's id with one letter on the end (the send
// endpoint only takes [a-z0-9], so no separator).
export const SEND_PARTS = ['day', 'night', 'summary'];
const PART_SUFFIX = { day: 'd', night: 'n', summary: 's' };
export const PART_TITLE = {
    day: 'Send day field ticket (combined)',
    night: 'Send night field ticket (combined)',
    summary: 'Send week summary field ticket',
};
// The same three, short enough to name a send inside a sentence or a hover.
export const PART_SHORT = { day: 'Day crew', night: 'Night crew', summary: 'Week summary' };
export const partDocId = (id, part) => `${id}${PART_SUFFIX[part] || ''}`;

/** The cells one send covers: its own crew's, or all of them for the summary. */
export const partCells = (cells, part) => (part === 'summary'
    ? sortCells(cells)
    : sortCells(cells).filter((k) => splitCell(k).sh === (part === 'day' ? 'Day' : 'Night')));

/**
 * A week summary on its own: the summary send over days whose daily tickets
 * went out as documents of their own (a week's days split over two or three
 * tickets, each signed). Its days stay those tickets' — it covers no cell on
 * the map or the day chips (coverageMap), and the dates' signed stage stays
 * theirs (signedByDate); the Reconciliation view reads it as the days'
 * weekly ticket (billingRecon.statusContext).
 */
export const isSummaryDoc = (doc) => doc?.kind === 'summary';

// ── Supplements ──
// A ticket for people a signed ticket missed: it names who is on it
// (`only`, [{ id, name }]) and may sit on days another ticket already owns.
// The packet prints those people alone — no equipment, no one else — so the
// client never signs the same hours twice. A supplement with nobody picked
// yet builds nothing.
export const isSupplementDoc = (doc) => doc?.kind === 'supplement';
export const onlyIds = (doc) => new Set((doc?.only || []).map((p) => String(p.id || '')).filter(Boolean));
export const onlyNames = (doc) => (doc?.only || []).map((p) => String(p.name || p.id || '')).filter(Boolean).join(', ');
/**
 * A day's ticket cut down to the named people: their labor rows, the phase
 * codes they touched, and no equipment. `only` empty or absent returns the
 * ticket untouched.
 */
export const narrowTicket = (t, only) => {
    if (!t || !Array.isArray(only) || !only.length) return t;
    const ids = new Set(only.map((p) => String(p.id || '')));
    const labor = (t.labor || []).filter((p) => ids.has(String(p.id)));
    const used = new Set();
    labor.forEach((p) => Object.keys(p.hours || {}).forEach((c) => { if (p.hours[c]) used.add(c); }));
    return { ...t, labor, equipment: [], codes: (t.codes || []).filter((c) => used.has(c.code)), dropped: [] };
};

/**
 * Which of the three sends a document actually has: a crew send for each
 * crew on it, and a summary once it spans more than a day or carries both
 * crews (one day, one crew, and the ticket IS the document). A week
 * summary on its own has only the summary.
 */
export const docParts = (doc) => {
    if (isSummaryDoc(doc)) return doc.cells?.length ? ['summary'] : [];
    const cells = sortCells(doc?.cells || []);
    const dates = new Set(cells.map((k) => splitCell(k).d));
    const shifts = new Set(cells.map((k) => splitCell(k).sh));
    // A supplement is a few people's day tickets; the week's summary
    // already went out on the ticket that missed them.
    return SEND_PARTS.filter((p) => (p === 'summary'
        ? !isSupplementDoc(doc) && (dates.size > 1 || shifts.size > 1)
        : partCells(cells, p).length > 0));
};

/** Who one send goes to — see docSigners for the shape. */
export const partSigners = (settings, doc, part) => {
    const cells = partCells(doc?.cells || [], part);
    const list = docSigners(settings, cells);
    if (part !== 'summary') return list;
    const shifts = new Set(sortCells(doc?.cells || []).map((k) => splitCell(k).sh));
    // Both crews on the document and the summary is the All Shifts cover,
    // which only the sign-everything people sign; one crew, and its cover
    // carries that crew's rep as well.
    return shifts.size > 1 ? list.filter((s) => s.role === 'All') : list;
};

/**
 * The envelope standing on each of a document's sends. A document sent
 * before the split carries one envelope under the document's own id — it
 * stands for every part, so an old ticket still reads as sent.
 */
export const partEnvelopes = (doc, sigByDoc) => {
    const legacy = (doc && sigByDoc?.[doc.id]) || null;
    const out = {};
    docParts(doc).forEach((p) => {
        const own = sigByDoc?.[partDocId(doc.id, p)] || legacy;
        // A week envelope sent outside the map stands on the crew part it
        // carried. The document's own send keeps the part unless it has died
        // and the week went back out under the week key instead.
        const week = weekEnvelope(doc, p, sigByDoc);
        out[p] = own && !(week && DEAD.includes(envStatus(own))) ? own : week;
    });
    return out;
};

const envStatus = (e) => String(e?.Status || '').toLowerCase();
const DEAD = ['voided', 'expired', 'failed'];

/**
 * The 'Week-Day' / 'Week-Night' envelope that carried one crew part of a
 * document, else null. The weekly packet prints a ticket for every day of its
 * week, so it covers the part when all of the part's cells fall in that one
 * week. The week summary has no week-keyed kind — it only ever goes out as
 * the document's own send.
 */
const weekEnvelope = (doc, part, sigByDoc) => {
    if (part !== 'day' && part !== 'night') return null;
    const weeks = new Set(partCells(doc?.cells || [], part).map((k) => weekEnding(splitCell(k).d)));
    if (weeks.size !== 1) return null;
    return sigByDoc?.[weekKey([...weeks][0], part === 'day' ? 'Day' : 'Night')] || null;
};

/**
 * Where a set of sends stands together: declined the moment one is, signed
 * only once every one of them is, and out as soon as one is. `expected` is
 * how many sends the set ought to have — one that has not gone out yet keeps
 * the set off Signed. A set whose envelopes have all died hands itself back
 * for correcting; null means nothing has gone out at all.
 */
export const stageAcross = (doc, list, expected) => {
    const live = (list || []).filter(Boolean);
    if (!live.length) return null;
    if (live.some((e) => envStatus(e) === 'declined')) return 'Declined';
    const alive = live.filter((e) => !DEAD.includes(envStatus(e)));
    if (!alive.length) return doc?.ai ? 'Validated' : 'Draft';
    const want = Number.isFinite(expected) ? expected : live.length;
    if (alive.length === want && alive.every((e) => envStatus(e) === 'completed')) return 'Signed';
    return 'Sent for Signature';
};

/**
 * Where a document stands across its three sends: signed once every one of
 * them is, declined the moment one is, and out as soon as one is.
 */
export const docStage = (doc, envs) => {
    const parts = docParts(doc);
    return stageAcross(doc, parts.map((p) => envs?.[p]), parts.length) || doc?.st;
};

// ── One cell's sends ──
//
// A work day chip is not the ticket: it is one date and one crew, and the
// client signs it twice over — once on the crew's combined document (the
// superintendent who runs that shift) and once on the week summary (the
// reps who sign everything). Two envelopes, never more, and the chip has to
// speak for both.

/**
 * The sends that carry one cell — at most two: its own crew's, and the week
 * summary when the document has one (docParts). A single-day, single-crew
 * document has no summary, so its cells ride one send.
 */
export const cellParts = (doc, sh) => {
    const parts = docParts(doc);
    return [sh === 'Night' ? 'night' : 'day', 'summary'].filter((p) => parts.includes(p));
};

/** How a status line ranks a cell's sends: the one furthest from done wins. */
const LEAD_RANK = { declined: 0, created: 1, sent: 2, delivered: 3, completed: 4 };

/**
 * Which of a cell's envelopes the status line speaks for: a declined one
 * first, then the one furthest from done — "Out for signature" on the
 * summary is the news when the crew's document is already signed. Takes
 * either [{ part, env }] or bare envelopes.
 */
export const leadEnvelope = (envs) => {
    const list = (envs || []).map((x) => (x && x.env !== undefined ? x.env : x)).filter(Boolean);
    if (!list.length) return null;
    const rank = (e) => (DEAD.includes(envStatus(e)) ? 9 : (LEAD_RANK[envStatus(e)] ?? 5));
    return list.slice().sort((a, b) => rank(a) - rank(b))[0];
};

/** Worst standing wins when one person is on both of a cell's sends. */
const SIGNER_RANK = { declined: 0, waiting: 1, opened: 2, signed: 3 };

/**
 * Who still owes a signature on one cell, across both of its sends. The reps
 * who sign everything are on the crew send AND the summary: they count once,
 * and read signed only once they have signed both — so "2/3 signed" on a day
 * chip is three people, not three signature lines. Dead envelopes are left
 * out; a voided send is not owed by anyone.
 */
export const cellProgress = (envs) => {
    const by = new Map();
    (envs || []).forEach((x) => {
        const env = x && x.env !== undefined ? x.env : x;
        if (!env || DEAD.includes(envStatus(env))) return;
        signerProgress(env).list.forEach((p) => {
            const key = String(p.email || p.name || '').toLowerCase();
            const prev = by.get(key);
            if (!prev || (SIGNER_RANK[p.status] ?? 1) < (SIGNER_RANK[prev.status] ?? 1)) by.set(key, { ...p });
        });
    });
    const list = [...by.values()];
    return {
        list,
        total: list.length,
        signed: list.filter((x) => x.status === 'signed').length,
        waiting: list.filter((x) => x.status === 'waiting' || x.status === 'opened'),
        declined: list.filter((x) => x.status === 'declined'),
    };
};

/** Locked once ANY of the three sends is out — the cells they name are fixed. */
export const docLockedAny = (doc, envs) => Boolean(doc?.st === 'Signed' && doc?.signed)
    || SEND_PARTS.some((p) => docLocked(doc, envs?.[p]));

// ── Work dates under a signed summary ──
//
// Cost history lines carry a work date but no crew shift, so the views that
// tie money to paper (the Add Lines picker) ask about the DATE: is the week
// summary that prints this day signed? That is the one send covering both
// crews, signed by the reps who sign everything — the client's word on the
// whole day, which is what an invoice line leans on.

const STAGE_RANK = { Declined: 0, Draft: 1, Validated: 2, 'Sent for Signature': 3, Signed: 4 };

/**
 * Where the summary over a document stands. A document with a week summary
 * send answers with that send alone — the crew sends coming back does not
 * sign the summary. A single-day, single-crew document has no summary (the
 * ticket IS the document), so it answers with its own stage, and a signed
 * copy uploaded by hand signs the whole document either way.
 */
export const summaryStage = (doc, sigByDoc) => {
    if (doc?.st === 'Signed' && doc?.signed) return 'Signed';
    const envs = partEnvelopes(doc, sigByDoc);
    if (!docParts(doc).includes('summary')) return docStage(doc, envs);
    return stageAcross(doc, [envs.summary], 1) || (doc.st === 'Signed' ? 'Sent for Signature' : doc.st);
};

/**
 * Work date -> { stage, docs: [{ id, name, stage, at }] } for every date a
 * ticket document covers. Two documents on one date (a day-crew document and
 * a night-crew one) both have to be signed — the date reads as the one
 * furthest from done. Dates no document covers are absent.
 */
export const signedByDate = (docs, sigByDoc) => {
    const out = {};
    // A week summary on its own leaves its days' stage to their tickets.
    (docs || []).filter((doc) => !isSummaryDoc(doc)).forEach((doc) => {
        const stage = summaryStage(doc, sigByDoc);
        const envs = partEnvelopes(doc, sigByDoc);
        const env = envs.summary || leadEnvelope(Object.values(envs));
        const entry = {
            id: doc.id,
            name: docName(doc.cells),
            stage,
            at: stage === 'Signed' ? String(env?.Completed_At_Utc || doc.signed?.t || '') : '',
        };
        new Set(doc.cells.map((k) => splitCell(k).d)).forEach((d) => {
            const e = out[d] || (out[d] = { stage, docs: [] });
            e.docs.push(entry);
            if ((STAGE_RANK[stage] ?? 1) < (STAGE_RANK[e.stage] ?? 1)) e.stage = stage;
        });
    });
    return out;
};

// ── Test sends ──
//
// A test envelope carries the real packet but goes only to the person
// pressing the button, and rides Doc_ID 'test<docId>' — so envelopesByDoc
// files it under a key no document owns: it never moves the ticket's stage,
// never freezes it, and the webhook skips filing its signed copy.

export const TEST_DOC_PREFIX = 'test';
export const testDocId = (id) => `${TEST_DOC_PREFIX}${id}`;
export const isTestDocId = (id) => String(id || '').toLowerCase().startsWith(TEST_DOC_PREFIX);

/** The one recipient of a test send: me, holding every signature line the packet prints. */
export const testSigners = (settings, cells, me) => {
    const email = String(me?.email || '').trim();
    if (!emailOk(email)) return [];
    const signers = jobSigners(settings);
    const anchors = [];
    sortCells(cells).map((k) => splitCell(k)).forEach(({ d, sh }) => signerSlots(signers, sh, 'full', d).forEach((slot) => {
        if (!anchors.includes(slot.label)) anchors.push(slot.label);
    }));
    return [{ name: String(me?.name || '').trim() || email, email, role: 'All', anchors }];
};

/**
 * Where each signer stands on one envelope: [{ name, email, role, status,
 * at }] with status signed | declined | opened | waiting. Envelopes from
 * before the recipient table read as their one Signer_* person, standing
 * where the envelope stands.
 */
export const signerProgress = (env) => {
    const recs = Array.isArray(env?.recipients) && env.recipients.length ? env.recipients : null;
    const norm = (st) => {
        const s = String(st || '').toLowerCase();
        if (s === 'completed') return 'signed';
        if (s === 'declined') return 'declined';
        if (s === 'delivered') return 'opened';
        return 'waiting';
    };
    const list = recs
        ? recs.map((r) => ({
            name: r.Name || r.Email, email: r.Email, role: r.Role || null,
            status: norm(r.Status),
            at: r.Signed_At_Utc || r.Declined_At_Utc || r.Delivered_At_Utc || null,
            reason: r.Declined_Reason || null,
        }))
        : env?.Signer_Email
            ? String(env.Signer_Email).split(/[,;]/).map((e) => e.trim()).filter(Boolean).map((email, i, all) => ({
                name: all.length === 1 ? (env.Signer_Name || email) : email, email, role: null,
                status: norm(env.Status),
                at: env.Completed_At_Utc || env.Declined_At_Utc || env.Delivered_At_Utc || null,
                reason: env.Declined_Reason || null,
            }))
            : [];
    const signed = list.filter((x) => x.status === 'signed');
    return {
        list, total: list.length, signed: signed.length,
        waiting: list.filter((x) => x.status === 'waiting' || x.status === 'opened'),
        declined: list.filter((x) => x.status === 'declined'),
    };
};

// ── Approval to send ──
//
// Nothing goes to the client until someone on the Strike side has looked at
// the finished packet and said so. The approval is pinned to the validation
// hash it was given on: change a person's hours, a shift, or the backup and
// it has to be given again.

/** Why the packet can't go out on approval grounds — [] means it can. */
export const approvalBlockers = (doc, currentHash) => {
    if (!doc?.ap) return ['Needs an approval before it goes to the client.'];
    if (currentHash && doc.ap.hash !== currentHash) return ['The ticket changed since it was approved — approve it again.'];
    return [];
};

/** The approver emails on the job's settings, lowercased. Blank = anyone. */
export const approverList = (settings) => String(settings?.approvers || '')
    .split(/[,;\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);

/** Whether this user may approve packets on this job. */
export const canApprove = (settings, userEmail) => {
    const list = approverList(settings);
    if (!list.length) return true;
    return list.includes(String(userEmail || '').trim().toLowerCase());
};

// ── Review freshness ──

// FNV-1a over a canonical JSON string — stable, dependency-free, and enough
// to notice that a person's hours or a file choice changed since the review.
export const hashString = (s) => {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(36);
};

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * The fingerprint a review is valid for: every covered cell's ticket (who,
 * what class, hours per code, flags) plus the chosen backup. cellTickets is
 * { '<cell>': shiftTicket }.
 */
export const validationHash = (cells, cellTickets, files) => {
    const canon = sortCells(cells).map((k) => {
        const t = cellTickets?.[k] || { labor: [], equipment: [] };
        return [k,
            (t.labor || []).map((p) => [String(p.id), p.cls || '', r2(p.total), r2(p.ot), p.pd ? 1 : 0, p.tp ? 1 : 0, p.rr ? 1 : 0,
                Object.keys(p.hours || {}).sort().map((c) => [c, r2(p.hours[c])])])
                .sort((a, b) => a[0].localeCompare(b[0]) || String(a[3]).localeCompare(String(b[3]))),
            (t.equipment || []).map((e) => [String(e.unit || e.desc), r2(e.total)]).sort(),
        ];
    });
    const f = (files || []).map((x) => String(x.id)).sort();
    return hashString(JSON.stringify([canon, f]));
};

/**
 * The document's current review per cell, from readFieldTicketReviews rows:
 * the run recorded on the document (ai.runs) when it is still there, else
 * nothing — an older or someone else's orphaned run doesn't count.
 */
export const reviewsByCell = (doc, reviews) => {
    const byId = {};
    (reviews || []).forEach((r) => { byId[r.id] = r; });
    const out = {};
    Object.entries(doc?.ai?.runs || {}).forEach(([k, id]) => { if (byId[id]) out[k] = byId[id]; });
    return out;
};

export const ackKey = (review, i) => `${review.id}#${i}`;

export const countFindings = (doc, byCell) => {
    const c = { fail: 0, warn: 0, info: 0, open: 0 };
    (doc?.cells || []).forEach((k) => {
        const r = byCell?.[k];
        (r?.result?.findings || []).forEach((x, i) => {
            if (c[x.level] !== undefined) c[x.level] += 1;
            if (x.level === 'fail' && !doc.ack?.[ackKey(r, i)]) c.open += 1;
        });
    });
    return c;
};

/**
 * Why the document can't be sent yet — [] means it can. The review is a hard
 * gate: it has to have run on exactly this data, finished for every cell, and
 * every failure has to carry an acknowledgment.
 */
export const sendBlockers = (doc, byCell, currentHash) => {
    const out = [];
    if (!doc?.cells?.length) return ['Pick at least one day or shift for this ticket.'];
    if (!doc.ai || !Object.keys(doc.ai.runs || {}).length) return ['Run the AI check before sending.'];
    if (currentHash && doc.ai.hash !== currentHash) out.push('Hours, shifts or backup changed since the check — run it again.');
    doc.cells.forEach((k) => {
        const r = byCell?.[k];
        const { d, sh } = splitCell(k);
        if (!r) out.push(`The check has not run for ${md(d)} ${sh}.`);
        else if (r.status === 'running') out.push(`The check is still running for ${md(d)} ${sh}.`);
        else if (r.status === 'error') out.push(`The check failed for ${md(d)} ${sh} — ${r.error || 'unknown error'}`);
    });
    const { open } = countFindings(doc, byCell);
    if (open) out.push(`${open} failed ${open === 1 ? 'item needs' : 'items need'} a fix or an acknowledgment.`);
    return out;
};

/** The file name a document's packet (and its signed copy) goes by. */
export const docFileName = (cells, job, opts = {}) => {
    const tok = (d) => moment(d, 'YYYY-MM-DD').format('M.D.YY');
    const byShift = { Day: [], Night: [] };
    sortCells(cells).forEach((k) => { const { d, sh } = splitCell(k); byShift[sh].push(d); });
    const span = (list) => (list.length === 1 ? tok(list[0]) : `${tok(list[0])}-${tok(list[list.length - 1])}`);
    const all = [...new Set([...byShift.Day, ...byShift.Night])].sort();
    const shifts = byShift.Day.length && byShift.Night.length ? 'Day+Night' : byShift.Day.length ? 'Day' : 'Night';
    // The job number rides in the name so a packet saved out of the browser
    // still says which job it belongs to (Downloads folders mix jobs).
    const j = String(job || '').trim();
    const sup = opts.supplement ? ' Supplement' : '';
    return all.length
        ? `Field Tickets${j ? ` ${j}` : ''} ${span(all)} ${shifts}${sup}.pdf`
        : `Field Tickets${j ? ` ${j}` : ''}${sup}.pdf`;
};

// ── Backup suggestions ──

const READABLE = /\.(pdf|jpe?g|png|gif|webp|xlsx|xlsm|xls|csv|docx?|pptx?|rtf|odt)$/i;

/**
 * SharePoint files that look like backup for the chosen cells: dated (by
 * name or week/day folder) inside the covered dates, with a shift word that
 * doesn't contradict the covered shifts. Ranked sign-in sheets first.
 */
export const suggestBackup = (files, cells, fileDate) => {
    const want = {};
    sortCells(cells).forEach((k) => { const { d, sh } = splitCell(k); (want[d] = want[d] || new Set()).add(sh); });
    const score = (f) => {
        const d = fileDate(f);
        if (!d || !want[d]) return -1;
        const text = `${f.path || ''}/${f.name || ''}`;
        const night = /night/i.test(text); const day = /\bdays?\b/i.test(text);
        if (night && !day && !want[d].has('Night')) return -1;
        if (day && !night && !want[d].has('Day')) return -1;
        if (/^signed field ticket|^multi phase field ticket|force report/i.test(String(f.name || ''))) return -1;
        // Only what the review can actually read (validateFieldTicket).
        if (!READABLE.test(String(f.name || ''))) return -1;
        let s = 1;
        if (/sign\s*.?in/i.test(text)) s += 2;
        if (/signed/i.test(text)) s += 1;
        if (/timesheet|time\s*sheet/i.test(text)) s += 1;
        if (/\.(pdf|jpe?g|png)$/i.test(String(f.name || ''))) s += 1;
        return s;
    };
    return (files || [])
        .map((f) => ({ f, s: score(f) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s || String(a.f.name).localeCompare(String(b.f.name)))
        .map((x) => x.f);
};

// What auto-attach will put on a ticket without asking: scans only, and only
// ones filed as that day's paper — named for the day or sitting in the day's
// folder. A file whose only date is its week-ending folder (a weekly report,
// a compare PDF) stays a suggestion; so do workbooks, which duplicate a scan.
const SCAN = /\.(pdf|jpe?g|png)$/i;
const NOT_BACKUP = /heavy\s*job|duplicates?\b|^master\b/i;
const WEEK_FOLDER = /^\s*(w\.?\s*e\b|week)/i;

const dayFiled = (f, d, fileDate) => {
    if (fileDate({ ...f, path: '' }) === d) return true;
    const dated = String(f.path || '').split('/').reverse().find((seg) => fileDate({ name: seg, path: '' }));
    return Boolean(dated) && !WEEK_FOLDER.test(dated) && fileDate({ name: dated, path: '' }) === d;
};

/**
 * Backup to attach on its own, per covered cell that hasn't been filled yet.
 * A cell counts as filled once something was attached for it (doc.af) or the
 * check already ran on it — so a scan filed later lands in the suggestions
 * instead of silently changing a checked ticket. Files the user took off
 * (doc.skip) never come back. Returns { files, af } to merge, or null.
 */
export const autoBackup = (doc, files, fileDate) => {
    if (!doc?.cells?.length || !(files || []).length) return null;
    const have = new Set((doc.files || []).map((f) => String(f.id)));
    const skip = new Set((doc.skip || []).map(String));
    const done = new Set((doc.af || []).filter((k) => doc.cells.includes(k)));
    const ran = doc.ai?.runs || {};
    const add = [];
    const filled = [];
    sortCells(doc.cells).forEach((k) => {
        if (done.has(k) || ran[k]) return;
        const { d } = splitCell(k);
        const picks = suggestBackup(files, [k], fileDate)
            .filter((f) => SCAN.test(String(f.name || '')) && !NOT_BACKUP.test(String(f.name || '')) && dayFiled(f, d, fileDate));
        if (!picks.length) return;
        filled.push(k);
        picks.forEach((f) => {
            const id = String(f.id);
            if (have.has(id) || skip.has(id)) return;
            have.add(id);
            add.push({ id: f.id, driveId: f.driveId, name: f.name, path: f.path || '', auto: 1 });
        });
    });
    if (!filled.length) return null;
    return { files: add, af: sortCells([...done, ...filled]) };
};

/** Covered cells with no chosen backup dated on their day. */
export const cellsWithoutBackup = (doc, fileDate) => {
    const dated = new Set((doc?.files || []).map((f) => fileDate(f)).filter(Boolean));
    return sortCells(doc?.cells || []).filter((k) => !dated.has(splitCell(k).d));
};

// ── What Claude reviews for one cell ──

const shiftSourceOf = (id, dayOv, rosterShift, p) => {
    if (dayOv && dayOv[id]) return 'manual correction';
    if (rosterShift && rosterShift[id]) return 'attendance roster';
    return p.shift ? 'HeavyJob card' : 'none';
};

/**
 * The ticket and context sent with one cell's review. Everything is plain
 * data the model can quote back — ids as strings, hours rounded, and only
 * the slices of the job-wide maps that could matter.
 *
 * input: { d, sh, ticket (the cell's shift ticket), dayTicket (both shifts),
 *   dayRows, dayOv, rosterShift, roster, staffCls, validation (the job's
 *   HJ-vs-HEET rows), qa (billingQa runChecks areas), prev: { d, ticket } }
 */
export const cellPayload = (input) => {
    const {
        d, sh, ticket, dayTicket, dayRows, dayOv, rosterShift, roster, staffCls, validation, qa, prev,
    } = input;
    const t = ticket || { codes: [], labor: [], equipment: [] };
    const person = (p) => ({
        id: String(p.id || ''),
        name: p.name,
        classification: p.cls || '',
        payClass: p.payClass || '',
        crewLead: p.crewLead || '',
        shift: p.night ? 'Night' : 'Day',
        shiftSource: shiftSourceOf(String(p.id), dayOv, rosterShift, p),
        hoursByCode: Object.fromEntries(Object.entries(p.hours || {}).filter(([, h]) => h).map(([c, h]) => [c, r2(h)])),
        total: r2(p.total),
        overtime: r2(p.ot),
        perDiem: Boolean(p.pd), truckPay: Boolean(p.tp), rigPay: Boolean(p.rr),
    });
    const other = sh === 'Night' ? 'Day' : 'Night';
    const otherCrew = (dayTicket?.labor || []).filter((p) => Boolean(p.night) === (other === 'Night'));
    const onTicket = new Set(t.labor.map((p) => String(p.id)));
    const lump = (dayRows || []).some((r) => String(r.Staff_ID) === '999999' || String(r.Staff_Name || '').trim() === 'Miscellaneous');
    return {
        ticket: {
            date: d,
            shift: sh,
            phaseCodes: t.codes.map((c) => ({ code: c.code, description: c.desc || '' })),
            labor: t.labor.map(person),
            equipment: t.equipment.map((e) => ({
                unit: e.unit, description: e.desc, rental: Boolean(e.rental), total: r2(e.total),
                hoursByCode: Object.fromEntries(Object.entries(e.hours || {}).filter(([, h]) => h).map(([c, h]) => [c, r2(h)])),
            })),
            totals: {
                people: t.labor.length,
                laborHours: r2(t.labor.reduce((a, p) => a + p.total, 0)),
                equipmentHours: r2(t.equipment.reduce((a, e) => a + e.total, 0)),
            },
        },
        context: {
            otherShiftSameDate: otherCrew.map((p) => ({ id: String(p.id || ''), name: p.name, total: r2(p.total) })),
            attendanceRoster: (roster || []).map((r) => ({ id: String(r.id || ''), name: r.name || '', shift: r.shift || '', crewLead: r.lead || r.crewLead || '' })),
            staffListClassifications: Object.fromEntries(Object.entries(staffCls || {})
                .filter(([id]) => onTicket.has(String(id)) || (roster || []).some((r) => String(r.id) === String(id)))),
            manualShiftCorrections: dayOv || {},
            anonymousDirectorLumpPresent: lump,
            heavyJobVsHeet: (validation || []).filter((v) => v.Work_Date === d).map((v) => ({
                id: String(v.Staff_ID || ''), name: v.Staff_Name || '', heavyJobHours: v.HJ_Hours, heetHours: v.HEET_Hours, kind: v.Kind,
            })),
            automatedChecks: (qa || []).flatMap((a) => (a.items || [])
                .filter((x) => x.level !== 'info' || x.tag)
                .map((x) => ({ area: a.title, level: x.level, text: x.text, detail: (x.lines || []).slice(0, 15) }))),
            previousWorkDay: prev && prev.ticket ? {
                date: prev.d,
                crew: prev.ticket.labor.filter((p) => Boolean(p.night) === (sh === 'Night')).map((p) => ({
                    id: String(p.id || ''), name: p.name, classification: p.cls || '', total: r2(p.total),
                    hoursByCode: Object.fromEntries(Object.entries(p.hours || {}).filter(([, h]) => h).map(([c, h]) => [c, r2(h)])),
                    perDiem: Boolean(p.pd),
                })),
            } : null,
        },
    };
};

/**
 * Rule findings across the document's cells that no single-cell review can
 * see: one person on both crews of a date, and a day over 16 hours. Keyed by
 * the cell they attach to; they are warnings, never a gate.
 */
export const crossCellFindings = (cellTickets) => {
    const out = {};
    const byDate = {};
    Object.entries(cellTickets || {}).forEach(([k, t]) => {
        const { d } = splitCell(k);
        (t?.labor || []).forEach((p) => {
            // The anonymous salaried lump is several people's day, not one.
            if (String(p.id) === '999999' || String(p.name || '').trim() === 'Miscellaneous') return;
            const id = String(p.id || p.name);
            const day = (byDate[d] = byDate[d] || {});
            (day[id] = day[id] || []).push({ k, p });
        });
    });
    Object.values(byDate).forEach((people) => Object.values(people).forEach((list) => {
        const total = list.reduce((a, x) => a + (x.p.total || 0), 0);
        const { p } = list[0];
        if (new Set(list.map((x) => splitCell(x.k).sh)).size > 1) {
            list.forEach((x) => (out[x.k] = out[x.k] || []).push({
                level: 'warn', category: 'shift', staffId: String(p.id || ''), name: p.name,
                ticketValue: list.map((y) => `${splitCell(y.k).sh} ${r2(y.p.total)}h`).join(' + '), backupValue: '',
                evidence: 'Ticket data', text: 'On both the day and night ticket for the same date.',
                fix: 'Confirm which crew they worked, or that the split is real.', rule: true,
            }));
        } else if (total > 16) {
            (out[list[0].k] = out[list[0].k] || []).push({
                level: 'warn', category: 'hours', staffId: String(p.id || ''), name: p.name,
                ticketValue: `${r2(total)}h`, backupValue: '', evidence: 'Ticket data',
                text: 'More than 16 hours in one day.', fix: 'Confirm against the sign-in sheet.', rule: true,
            });
        }
    }));
    return out;
};

// ── The reviewed weekly time card's equipment ──
// Until a week's time card has been reviewed, the tickets bill the equipment
// HeavyJob posted. Once the reviewed workbook exists (the Labor + Equipment
// workbook the field ticket packet scripts take as SHEET_FILE), its Equipment
// tab IS the billed equipment: a unit on rent bills the calendar day at 24
// hours, which HeavyJob's per-card hours never say. Stored per week ending,
// chunked like a document, so every rebuild of that week bills the same.
//
// Kept to equipment on purpose — labor still comes off the HeavyJob feed, so
// there is only one place the crew and its hours can come from.

/** The phase code the reviewed workbook's equipment bills on. */
export const EQUIP_PHASE = { code: '100101', desc: 'Project Team' };

/** Rows for one week's reviewed equipment. `we` is the Sunday, YYYY-MM-DD. */
export const equipWeekRows = (we, units) => chunkRows('tcequip', `tcequip_${we}`, {
    id: we, we, units, at: moment().toISOString(),
});

/** Drops a week's reviewed equipment back to the HeavyJob basis. */
export const removedEquipWeekRows = (we) => [{
    fieldName: 'tcequip', rowId: `tcequip_${we}#0`, value: '1/1', notes: JSON.stringify({ id: we, we, del: 1 }),
}];

/** Reviewed equipment by week ending: { [we]: { we, units, at } }. */
export const parseEquipWeeks = (userRows) => {
    const out = {};
    parseChunked(userRows, 'tcequip').forEach((w) => {
        if (!w || w.del || !Array.isArray(w.units)) return;
        out[String(w.we || w.id)] = { we: String(w.we || w.id), units: w.units, at: w.at || '' };
    });
    return out;
};

/**
 * One date's equipment rows in the shape ticketFrom returns, or null when
 * that date's week has no reviewed workbook. Every unit bills its whole day
 * on the one phase code, exactly as the packet script's SHEET branch does.
 */
export const equipForDate = (equipWeeks, d) => {
    const m = moment(d, 'YYYY-MM-DD');
    const we = (m.day() === 0 ? m : m.clone().day(7)).format('YYYY-MM-DD');
    const wk = equipWeeks?.[we];
    if (!wk) return null;
    return wk.units
        .filter((u) => Number(u.days?.[d]) > 0)
        .map((u) => ({
            unit: String(u.unit || ''),
            desc: String(u.desc || ''),
            rental: Boolean(u.rental),
            hours: { [EQUIP_PHASE.code]: Number(u.days[d]) },
            total: Number(u.days[d]),
        }));
};

/**
 * The Equipment tab of a reviewed weekly time card workbook, as stored units.
 * Header row is "Equipment | Unit No | Type | <7 day columns> | Total | Days",
 * and the day columns are read positionally off the week ending rather than
 * off their labels, which are written for people ("Mon\n9/7").
 */
export const parseEquipSheet = (grid, we) => {
    const head = (grid || []).findIndex((r) => String(r?.[0] || '').trim().toLowerCase() === 'equipment');
    if (head < 0) throw new Error('no Equipment tab header row — expected a row starting "Equipment"');
    const dates = Array.from({ length: 7 }, (_, i) => moment(we, 'YYYY-MM-DD').subtract(6 - i, 'days').format('YYYY-MM-DD'));
    const units = [];
    for (let i = head + 1; i < grid.length; i += 1) {
        const r = grid[i];
        const label = String(r?.[0] || '').trim();
        if (!label || /^totals?$/i.test(label)) continue;
        const days = {};
        dates.forEach((d, k) => {
            const h = Number(String(r[3 + k] ?? '').replace(/[$,\s]/g, ''));
            if (Number.isFinite(h) && h) days[d] = h;
        });
        if (!Object.keys(days).length) continue;
        units.push({
            desc: label, unit: String(r[1] ?? '').trim(),
            rental: /rental/i.test(String(r[2] ?? '')), days,
        });
    }
    if (!units.length) throw new Error('the Equipment tab has no units with hours');
    return units;
};

// ── Went out, or still a draft ──
//
// The day list is the tracker of what the CLIENT has in hand: a document that
// has only been drafted in the Ticket Map has no business colouring a day's
// chip, or a draft somebody started and forgot reads as "the ticket" for that
// day. The Ticket Map itself still shows every document, drafts included.

/** True once a document has been put in front of the client in any form. */
export const docWentOut = (doc, sigByDoc) => {
    if (!doc) return false;
    if (doc.st === 'Signed' && doc.signed) return true;
    const envs = partEnvelopes(doc, sigByDoc);
    if (Object.values(envs).some(Boolean)) return true;
    return ['Sent for Signature', 'Signed', 'Declined'].includes(doc.st);
};

// ── Where the packet pulls from ──
//
// A ticket is put together from several sources that disagree with each
// other by design: HeavyJob is what the field keyed, the reviewed time card
// is what project controls approved against the sign-in sheets, the
// attendance roster is who the client says was on which crew, the Staff List
// workbook is what each person bills as. The builder has a standing order of
// preference (the approved thing wins), but a ticket sometimes has to be cut
// another way — a week whose reviewed card is known wrong, a job where the
// roster is never filled in — and the person building it has to be able to
// say so, and have the choice recorded with the packet.
//
// Stored on the document as `src` and omitted at the defaults, because the
// document rides 900-char Notes chunks.

export const SRC_FIELDS = [
    {
        key: 'labor',
        label: 'Labor hours',
        options: [
            { id: 'auto', label: 'Reviewed time card, else HeavyJob', hint: 'Project controls’ approved weekly card where one is filed for the week; the HeavyJob feed (HEET, actual cost, or the raw card) where none is.' },
            { id: 'card', label: 'Reviewed time card only', hint: 'Refuse to build a day the reviewed card does not cover — nothing goes to the client on unreviewed hours.' },
            { id: 'hj', label: 'HeavyJob feed only', hint: 'Ignore the reviewed card for this ticket and bill exactly what the field keyed.' },
        ],
    },
    {
        key: 'shift',
        label: 'Day / night split',
        options: [
            { id: 'auto', label: 'Card → roster → HeavyJob card', hint: 'The reviewed card’s Shift column first, then the attendance roster’s Days or Nights, then the HeavyJob card the hours were keyed on. Manual corrections on the day always win.' },
            { id: 'roster', label: 'Roster → HeavyJob card', hint: 'Leave the reviewed card’s shift out — use the client’s attendance roster, then the HeavyJob card. Manual corrections still win.' },
            { id: 'hj', label: 'HeavyJob card only', hint: 'The shift each card was keyed on, and nothing else but the day’s manual corrections.' },
        ],
    },
    {
        key: 'cls',
        label: 'Classifications',
        options: [
            { id: 'auto', label: 'Card → roster → Staff List', hint: 'The reviewed card’s classification first, then the roster’s rate-sheet column, then the Staff List workbook’s Tier 4.' },
            { id: 'workbook', label: 'Staff List workbook only', hint: 'Bill each person as the field’s Multi Phase Field Ticket workbook maps them, whatever the card or roster say.' },
        ],
    },
    {
        key: 'equip',
        label: 'Equipment',
        options: [
            { id: 'auto', label: 'Reviewed workbook, else HeavyJob', hint: 'The reviewed weekly time card’s Equipment tab where one has been loaded for the week (a unit on rent bills the calendar day); HeavyJob’s equipment lines where not.' },
            { id: 'hj', label: 'HeavyJob only', hint: 'Equipment hours exactly as HeavyJob posted them, even where a reviewed workbook is loaded.' },
        ],
    },
    {
        key: 'backup',
        label: 'Backup pages',
        options: [
            { id: 'mapped', label: 'Pages matched to these days', hint: 'Where Claude’s check mapped a scan’s pages to dates, only the pages for the days on this ticket are bound in, turned upright. Unmapped files ride along whole.' },
            { id: 'all', label: 'Every page of every file', hint: 'Bind every page of every chosen backup file, in file order, as scanned.' },
        ],
    },
];
export const SRC_DEFAULT = Object.fromEntries(SRC_FIELDS.map((f) => [f.key, f.options[0].id]));

/** A document's source choices, defaults filled in and junk dropped. */
export const normalizeSrc = (src) => {
    const out = { ...SRC_DEFAULT };
    SRC_FIELDS.forEach((f) => {
        const v = src?.[f.key];
        if (f.options.some((o) => o.id === v)) out[f.key] = v;
    });
    out.notes = String(src?.notes || '').slice(0, 600);
    return out;
};

/** What to store: only the choices off the default, and the notes. */
export const compactSrc = (src) => {
    const n = normalizeSrc(src);
    const out = {};
    SRC_FIELDS.forEach((f) => { if (n[f.key] !== SRC_DEFAULT[f.key]) out[f.key] = n[f.key]; });
    if (n.notes) out.notes = n.notes;
    return Object.keys(out).length ? out : undefined;
};

/** True when any choice is off the default — the panel says so up front. */
export const srcIsCustom = (src) => Boolean(compactSrc({ ...normalizeSrc(src), notes: '' }));

/** "Labor hours: HeavyJob feed only · Backup pages: Every page …" — the choices off default. */
export const srcSummary = (src) => {
    const n = normalizeSrc(src);
    return SRC_FIELDS
        .filter((f) => n[f.key] !== SRC_DEFAULT[f.key])
        .map((f) => `${f.label}: ${f.options.find((o) => o.id === n[f.key])?.label || n[f.key]}`)
        .join(' · ');
};

// ── The packet's manifest ──
//
// What goes to the archive beside the PDF: enough to open the right packet
// later and to see what it was built from, without the PDF having to say.

/** FNV-1a over the PDF bytes, with the length — two builds of the same inputs match. */
export const bytesHash = (bytes) => {
    const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
    let h = 0x811c9dc5;
    for (let i = 0; i < b.length; i += 1) {
        h ^= b[i];
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return `${h.toString(36)}-${b.length.toString(36)}`;
};

export const fmtBytes = (n) => {
    const x = Number(n) || 0;
    if (x < 1024) return `${x} B`;
    if (x < 1024 * 1024) return `${Math.round(x / 1024)} KB`;
    return `${(x / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * The manifest for one built packet. `pk` is buildPacket's result ({ name,
 * bytes, skipped, labels, pages?, sources? }); `me` is { name, email }.
 */
export const packetManifest = (doc, pk, { part = 'all', purpose = 'preview', me, stage, sent, env } = {}) => {
    const cells = part === 'all' || part === 'tickets' ? sortCells(doc?.cells || []) : partCells(doc?.cells || [], part);
    const src = normalizeSrc(doc?.src);
    return {
        docId: doc?.id || '',
        part,
        purpose,
        name: pk?.name || '',
        hash: pk?.bytes ? bytesHash(pk.bytes) : '',
        pages: Number(pk?.pages) || 0,
        by: String(me?.name || '').slice(0, 120),
        byEmail: String(me?.email || '').slice(0, 200),
        stage: stage || doc?.st || '',
        covers: docName(cells),
        cells,
        files: (part === 'summary' ? [] : (doc?.files || [])).map((f) => ({ id: f.id, name: f.name, path: f.path || '', auto: f.auto ? 1 : 0 })),
        labels: pk?.labels || [],
        skipped: pk?.skipped || [],
        src,
        only: isSupplementDoc(doc) ? (doc.only || []).map((p) => ({ id: String(p.id || ''), name: String(p.name || '') })) : null,
        sources: pk?.sources || null,
        env: env || null,
        sent: sent || null,
        docRev: doc?.rev || 0,
        docHistory: (doc?.h || []).slice(-6),
    };
};

// ── Readiness ──
//
// Before a packet is built, everything the builder knows about its days that
// would make the ticket wrong is said in one list — the checks a careful
// person runs by hand and forgets one of.

const CHECK = (level, text, fix) => ({ level, text, fix });

/**
 * days: { [d]: daySources(d) } from the approval tab —
 *   { feed: 'HeavyJob cards (live)'|'HEET'|'actuals'|'HeavyJob'|'none', card: { name, modifiedBy? } | null,
 *     cardsApproved: 'approved/total' HeavyJob cards on a live day,
 *     cardLoading, cardError, mismatch: hours HEET-HJ, manual: n overrides,
 *     equipWorkbook: bool }
 * roster: { loading, error, found }.
 */
export const readinessChecks = (doc, days, roster, fileDate) => {
    const out = [];
    const cells = sortCells(doc?.cells || []);
    if (!cells.length) return [CHECK('fail', 'No days on this ticket yet.', 'Click day/shift cells on the map.')];
    const src = normalizeSrc(doc?.src);
    const dates = [...new Set(cells.map((k) => splitCell(k).d))].sort();
    const list = (ds) => ds.map(md).join(', ');
    if (roster?.error) out.push(CHECK('fail', `The attendance roster could not be read — ${roster.error}`, 'Nothing builds until it reads; sync again or fix the roster file in 02.05.'));
    else if (roster?.loading) out.push(CHECK('info', 'Still reading the attendance roster.', ''));
    else if (roster && roster.found === false && src.shift !== 'hj') out.push(CHECK('warn', 'No attendance roster on this job — the day/night split falls to the reviewed card and the HeavyJob card.', 'File Attendance & Force Report - Master under 02.05, or set the split to HeavyJob card only.'));
    if (dates.some((d) => days?.[d]?.feedLoading)) out.push(CHECK('info', 'Still reading which days have hours (HEET, actual cost, HeavyJob).', ''));
    const noFeed = dates.filter((d) => days?.[d]?.feed === 'none' && !days[d].feedLoading);
    if (noFeed.length) out.push(CHECK('fail', `No hours in any feed for ${list(noFeed)}.`, 'Those days will print no ticket. Take them off, or wait for HeavyJob.'));
    const liveUnapproved = dates.filter((d) => /live/.test(String(days?.[d]?.feed || '')) && /^0\//.test(String(days[d].cardsApproved || '')));
    if (liveUnapproved.length) out.push(CHECK('info', `${list(liveUnapproved)}: hours are the live HeavyJob cards, none approved in HeavyJob yet.`, 'Foremen can still edit them — rebuild right before sending.'));
    const rawHj = dates.filter((d) => days?.[d]?.feed === 'HeavyJob');
    if (rawHj.length) out.push(CHECK('warn', `${list(rawHj)}: hours are off the raw HeavyJob card — not yet in HEET, so unapproved straight time.`, 'Fine for a preview; for the client, wait for HEET or load the reviewed card.'));
    if (dates.some((d) => days?.[d]?.cardLoading)) out.push(CHECK('info', 'Still looking for the reviewed time card in SharePoint.', ''));
    const cardErr = dates.filter((d) => days?.[d]?.cardError);
    if (cardErr.length) out.push(CHECK('fail', `The reviewed time card could not be read for ${list(cardErr)}.`, 'Nothing builds on a failed read; sync again.'));
    const noCard = dates.filter((d) => days?.[d] && !days[d].cardLoading && !days[d].cardError && !days[d].card);
    if (src.labor === 'card' && noCard.length) out.push(CHECK('fail', `Reviewed time card only, but no card covers ${list(noCard)}.`, 'Load the card, or set Labor hours back to “Reviewed time card, else HeavyJob”.'));
    else if (src.labor === 'auto' && noCard.length && noCard.length < dates.length) out.push(CHECK('warn', `${list(noCard)} bill off HeavyJob while the other days bill off the reviewed card.`, 'Check the card covers every day, or the totals will mix two bases.'));
    else if (src.labor === 'auto' && noCard.length === dates.length) out.push(CHECK('info', 'No reviewed time card for these days — hours are HeavyJob’s.', 'Load the week’s reviewed card when project controls finishes it.'));
    if (src.labor === 'hj' && dates.some((d) => days?.[d]?.card)) out.push(CHECK('warn', 'HeavyJob only, but a reviewed time card exists for these days and is being ignored.', 'Say why in the notes — the client is invoiced from the card.'));
    const mism = dates.filter((d) => Math.abs(Number(days?.[d]?.mismatch) || 0) > 0.5);
    if (mism.length) out.push(CHECK('warn', `HEET and HeavyJob disagree on ${mism.map((d) => `${md(d)} (${Number(days[d].mismatch) > 0 ? '+' : ''}${Math.round(Number(days[d].mismatch) * 100) / 100}h)`).join(', ')}.`, 'Under is the morning pull catching up; over has been a double load. Rebuild after HEET settles.'));
    const manual = dates.filter((d) => Number(days?.[d]?.manual) > 0);
    if (manual.length) out.push(CHECK('info', `Manual shift corrections on ${manual.map((d) => `${md(d)} (${days[d].manual})`).join(', ')}.`, 'They win over every feed and are listed in the packet’s manifest.'));
    const equipMixed = dates.some((d) => days?.[d]?.equipWorkbook) && dates.some((d) => days?.[d] && !days[d].equipWorkbook);
    if (src.equip === 'auto' && equipMixed) out.push(CHECK('warn', 'Some days bill equipment off the reviewed workbook and some off HeavyJob.', 'Load the workbook for every week on the ticket, or set Equipment to HeavyJob only.'));
    if (doc.files?.length) {
        const bare = cellsWithoutBackup(doc, fileDate);
        if (bare.length) out.push(CHECK('warn', `No backup dated for ${bare.map((k) => { const { d, sh } = splitCell(k); return `${md(d)} ${sh}`; }).join(', ')}.`, 'Upload the sign-in scan, or the client signs those days on the ticket alone.'));
    } else {
        out.push(CHECK('warn', 'No backup on this ticket.', 'The sign-in scans are what the client checks the hours against.'));
    }
    if (src.backup === 'all' && doc.files?.length) out.push(CHECK('info', 'Every page of every backup file will be bound in, as scanned.', ''));
    if (srcIsCustom(src) && !src.notes) out.push(CHECK('warn', 'Sources are set off the default with no note saying why.', 'Write the reason in the notes — it is kept with every packet built from this ticket.'));
    return out;
};
